import { useMemo, useState } from 'react';
import { Badge, Button, Empty, EmptyDescription } from '@monksflow/monks-ui';
import { PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SearchInput } from '@/shared/ui';
import { twMerge } from 'tailwind-merge';
import { groupFormats } from '../lib/spec-rows';
import type { LibraryState } from '../model/types';

interface FormatTreeProps {
  state: LibraryState;
  selectedKey: string | null;
  onSelect: (key: string) => void;
  /** Opens New format, with `groupKey` preselected when the call comes from an empty group. */
  onNewFormat: (groupKey?: string) => void;
  onNewGroup: () => void;
}

/** The master pane: platform groups, each listing its formats, filtered by one search. */
export function FormatTree({
  state,
  selectedKey,
  onSelect,
  onNewFormat,
  onNewGroup,
}: FormatTreeProps) {
  const { t } = useTranslation('admin');
  const [query, setQuery] = useState('');
  const groups = useMemo(() => groupFormats(state, query), [state, query]);

  return (
    <nav aria-label={t('admin.adFormats.title')} className="flex h-full min-h-0 flex-col gap-sm">
      <SearchInput
        value={query}
        onChange={setQuery}
        placeholder={t('admin.adFormats.searchPlaceholder')}
        aria-label={t('admin.adFormats.searchLabel')}
      />
      <div className="min-h-0 flex-1 overflow-y-auto">
        {groups.length === 0 ? (
          <Empty>
            <EmptyDescription>{t('admin.adFormats.noMatches')}</EmptyDescription>
          </Empty>
        ) : (
          <ul className="flex flex-col gap-md">
            {groups.map(({ group, formats }) => (
              <li key={group.key}>
                <p className="type-label-sm px-xs pb-2xs text-muted-foreground">{group.name}</p>
                {formats.length === 0 && (
                  <p className="flex flex-wrap items-center gap-2xs px-xs text-body-sm text-muted-foreground">
                    {t('admin.adFormats.emptyGroup')}
                    <Button variant="ghost" size="sm" onClick={() => onNewFormat(group.key)}>
                      {t('admin.adFormats.addFormatToGroup', { group: group.name })}
                    </Button>
                  </p>
                )}
                <ul className="flex flex-col">
                  {formats.map((format) => (
                    <li key={format.key}>
                      <button
                        type="button"
                        aria-current={format.key === selectedKey ? 'true' : undefined}
                        onClick={() => onSelect(format.key)}
                        className={twMerge(
                          'flex w-full items-center gap-xs rounded-md px-xs py-2xs text-left text-body-sm',
                          'hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                          format.key === selectedKey ? 'bg-accent text-accent-foreground' : '',
                        )}
                      >
                        <span className="min-w-0 flex-1 truncate">{format.name}</span>
                        <span className="shrink-0 text-muted-foreground tabular-nums">
                          {format.w}×{format.h}
                        </span>
                        {format.status === 'deprecated' && (
                          <Badge variant="outline">{t('admin.adFormats.deprecated')}</Badge>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="grid grid-cols-2 gap-xs">
        <Button variant="outline" onClick={() => onNewFormat()}>
          <PlusIcon aria-hidden />
          {t('admin.adFormats.newFormat')}
        </Button>
        <Button variant="outline" onClick={onNewGroup}>
          <PlusIcon aria-hidden />
          {t('admin.adFormats.newGroup')}
        </Button>
      </div>
    </nav>
  );
}
